import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { BookOpenText } from '@phosphor-icons/react';

const KIND_GLYPH = {
  saw_fireworks:        '🎆',
  met_shy_bubble:       '👋',
  wandered_ocean:       '🌊',
  heard_voice_drift:    '🎙',
  dreamt_of_you:        '💭',
  caught_in_storm:      '🌪',
  floated_past_light:   '🗼',
  found_old_song:       '🎵',
  noticed_moon:         '🌙',
  kept_someone_company: '🤝',
};

/**
 * BubbleDiary — timeline of things the bubble noticed while you were gone.
 * Lives inside BubbleDetail. Owner-only. Marks all as seen on mount.
 */
const BubbleDiary = ({ bubble, isOwner }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/bubbles/${bubble.id}/diary?limit=50`);
      setEntries(data.entries || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    if (isOwner) {
      // Mark seen after a short delay so the user actually sees the "new" state
      const t = setTimeout(() => {
        api.post(`/bubbles/${bubble.id}/diary/seen`).catch(() => {});
      }, 4000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [bubble.id, isOwner]); // eslint-disable-line

  return (
    <div className="glass rounded-2xl p-5" data-testid="bubble-diary">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BookOpenText size={16} className="text-bx-cyan" weight="duotone" />
          <h3 className="font-display font-bold">What {bubble.name} noticed</h3>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-white/60">while you were gone</div>
      </div>
      {loading ? (
        <div className="text-white/60 text-sm">{bubble.name} is gathering their thoughts…</div>
      ) : entries.length === 0 ? (
        <div className="text-white/45 text-sm italic">
          {bubble.name} hasn't seen much yet. Come back tomorrow.
        </div>
      ) : (
        <div className="space-y-3">
          {entries.map((e) => (
            <div key={e.id} data-testid={`diary-${e.id}`}
              className={`flex items-start gap-3 ${!e.seen ? 'opacity-100' : 'opacity-75'}`}>
              <div className="text-2xl">{KIND_GLYPH[e.kind] || '·'}</div>
              <div className="flex-1">
                <div className={`text-sm italic ${!e.seen ? 'text-white' : 'text-white/70'}`}>
                  "{e.entry}"
                </div>
                <div className="text-[10px] uppercase tracking-widest text-white/35 mt-1 flex items-center gap-2">
                  <span>{new Date(e.ts).toLocaleString([], {month:'short', day:'numeric', hour:'numeric', minute:'2-digit'})}</span>
                  {e.world_event_kind && (
                    <span className="text-bx-cyan/80">· during {e.world_event_kind.replace(/_/g,' ')}</span>
                  )}
                  {e.imagined && <span className="text-white/60" data-testid={`diary-imagined-${e.id}`}>· imagined</span>}
                  {!e.seen && <span className="ml-auto text-bx-volt">new</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BubbleDiary;
